import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  spySerpService,
  type SaveSpyserpSegmentParams,
} from "@/services/SpySerpService";
import type { Segment } from "@/types/spyserp";

export const SPYSERP_SEGMENTS_QUERY_KEY = ["spyserp", "segments"] as const;

export interface SpyserpSegmentFilterOption {
  id: string;
  name: string;
  color: string | null;
}

export const toSpyserpSegmentFilterOptions = (
  segments: Segment[],
): SpyserpSegmentFilterOption[] =>
  [...segments]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((seg) => ({
      id: seg.id,
      name: seg.name,
      color: seg.color,
    }));

interface UseSpyserpSegmentsOptions {
  enabled?: boolean;
}

export const useSpyserpSegments = (options?: UseSpyserpSegmentsOptions) =>
  useQuery({
    queryKey: SPYSERP_SEGMENTS_QUERY_KEY,
    queryFn: () => spySerpService.getSegments(),
    staleTime: 1000 * 60 * 5,
    enabled: options?.enabled ?? true,
  });

export const useSaveSpyserpSegment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: SaveSpyserpSegmentParams) =>
      spySerpService.saveSegment(params),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: SPYSERP_SEGMENTS_QUERY_KEY,
      });
    },
  });
};

export const useDeleteSpyserpSegment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => spySerpService.deleteSegment(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: SPYSERP_SEGMENTS_QUERY_KEY,
      });
    },
  });
};
